import { ArrowPathIcon } from "@heroicons/react/24/outline";

import classNames from "../../../utils/classNames";

type OnPrimaryButtonVariant = "solid" | "outline";
type OnPrimaryButtonSize = "sm" | "md" | "lg";

interface OnPrimaryButtonProps {
  label: string;
  onClick?: () => void;
  /** "solid" = filled white button (primary action), "outline" = translucent button. */
  variant?: OnPrimaryButtonVariant;
  icon?: React.ReactNode;
  isSubmit?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  size?: OnPrimaryButtonSize;
  classes?: string;
}

/**
 * Button designed to sit on a primary-coloured surface (see {@link OnPrimarySelect}).
 * Use `solid` for the single primary action and `outline` for everything else.
 */
export default function OnPrimaryButton({
  label,
  onClick,
  variant = "outline",
  icon,
  isSubmit = false,
  disabled = false,
  isLoading = false,
  size = "md",
  classes = "",
}: OnPrimaryButtonProps) {
  const sizeClasses: Record<OnPrimaryButtonSize, string> = {
    sm: "px-2 py-1.5 text-sm",
    md: "px-3 py-2.5 text-sm",
    lg: "px-4 py-3 text-base",
  };

  const variantClasses: Record<OnPrimaryButtonVariant, string> = {
    solid: "bg-white text-primary-700 hover:bg-primary-50 shadow-sm disabled:bg-white/60",
    outline: "border border-white/25 bg-white/10 text-white hover:bg-white/20 disabled:bg-white/5",
  };

  return (
    <button
      type={isSubmit ? "submit" : "button"}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={classNames(
        "flex w-full items-center gap-2 rounded-md font-medium leading-4 transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-white/60 disabled:cursor-not-allowed disabled:opacity-70",
        sizeClasses[size],
        variantClasses[variant],
        classes
      )}
    >
      {isLoading ? (
        <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>
      )}
      <span>{label}</span>
    </button>
  );
}
